import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { sleep } from '@/utils/rate-limiter';

interface CookieCategories {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsent {
  consentGiven: boolean;
  consentVersion: string;
  cookieCategories: CookieCategories;
  consentTimestamp: string | null;
}

const CONSENT_VERSION = '1.0';
const LOCAL_STORAGE_KEY = 'cookie_consent';

// Retry configuration for FK errors (race condition with auth.users visibility)
const RETRY_CONFIG = {
  maxAttempts: 3,
  baseDelayMs: 150, // Start with 150ms, then 300ms, then 600ms
  retryableErrorCodes: ['23503'], // FK violation - user not yet visible in connection pool
};

/**
 * Check if an error is retryable (FK constraint violation due to race condition)
 */
const isRetryableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  return code ? RETRY_CONFIG.retryableErrorCodes.includes(code) : false;
};

export const useCookieConsent = () => {
  const userQuery = useUser();
  const auth = userQuery.data;
  const user = auth?.user ?? null;
  const session = auth?.session ?? null;
  const authLoading = userQuery.isLoading ?? false;
  // User is only considered authenticated if we have both user AND session, and auth is not loading
  const isAuthenticated = !authLoading && !!user && !!session;
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchConsent = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences_cookies')
        .select('*')
        .eq('user_id', user.id)
        .order('consent_timestamp', { ascending: false })
        .limit(1)
        .maybeSingle(); // Use maybeSingle() to gracefully handle missing records

      if (data && !error) {
        const categories = data.cookie_categories as Partial<CookieCategories>;
        const consentGiven = data.consent_given;
        const consentVersion = data.consent_version;
        setConsent({
          consentGiven,
          consentVersion,
          cookieCategories: {
            necessary: true,
            analytics: categories.analytics ?? false,
            marketing: categories.marketing ?? false,
          },
          consentTimestamp: data.consent_timestamp,
        });
      } else if (error) {
        // Log actual errors (not "no rows" which is expected)
        console.error('Error fetching consent:', error);
      }
      // If data is null and no error, user simply hasn't set preferences yet
    } catch (error) {
      console.error('Error in fetchConsent:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Migrate localStorage consent to database when user becomes authenticated
  const migrateLocalStorageToDatabase = useCallback(async () => {
    if (!user) return false;

    const localConsent = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!localConsent) return false;

    try {
      const parsed = JSON.parse(localConsent) as CookieConsent;

      // Only migrate if consent was actually given
      if (!parsed.consentGiven) return false;

      console.log('[Cookie Consent] Migrating localStorage consent to database');

      // Cast cookie categories to match the expected Json type
      const cookieCategories = {
        necessary: parsed.cookieCategories.necessary,
        analytics: parsed.cookieCategories.analytics,
        marketing: parsed.cookieCategories.marketing
      } as const;

      const { error } = await supabase
        .from('user_preferences_cookies')
        .upsert({
          user_id: user.id,
          consent_given: parsed.consentGiven,
          consent_version: parsed.consentVersion || CONSENT_VERSION,
          cookie_categories: cookieCategories,
          consent_timestamp: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (!error) {
        // Successfully migrated - remove from localStorage
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        console.log('[Cookie Consent] Migration successful, localStorage cleared');
        return true;
      } else {
        console.error('[Cookie Consent] Migration failed:', error);
        return false;
      }
    } catch (error) {
      console.error('[Cookie Consent] Error parsing localStorage for migration:', error);
      return false;
    }
  }, [user]);

  useEffect(() => {
    // Wait for AuthContext to finish loading before checking consent
    if (authLoading) {
      return;
    }

    if (isAuthenticated) {
      // Authenticated user: check for localStorage consent to migrate, then fetch from database
      const initAuthenticatedConsent = async () => {
        // First, try to migrate any existing localStorage consent
        await migrateLocalStorageToDatabase();
        // Then fetch from database (will include migrated data)
        await fetchConsent();
      };
      initAuthenticatedConsent();
    } else {
      // Non-authenticated user: check localStorage
      const localConsent = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localConsent) {
        try {
          setConsent(JSON.parse(localConsent));
        } catch (error) {
          console.error('Error parsing local consent:', error);
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
      }
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, fetchConsent, migrateLocalStorageToDatabase]);

  const saveConsent = async (categories: { analytics: boolean; marketing: boolean }) => {
    const newConsent: CookieConsent = {
      consentGiven: true,
      consentVersion: CONSENT_VERSION,
      cookieCategories: {
        necessary: true, // Always true
        analytics: categories.analytics,
        marketing: categories.marketing,
      },
      consentTimestamp: new Date().toISOString(),
    };

    // Only save to database if we have a confirmed authenticated user with valid session
    if (isAuthenticated) {
      // Cast the cookie categories to Json type
      const cookieCategories = {
        necessary: true,
        analytics: categories.analytics,
        marketing: categories.marketing
      } as const;

      // Retry logic for FK errors (race condition with auth.users visibility after signup)
      let lastError: unknown = null;
      for (let attempt = 1; attempt <= RETRY_CONFIG.maxAttempts; attempt++) {
        try {
          const { error } = await supabase
            .from('user_preferences_cookies')
            .upsert({
              user_id: user.id,
              consent_given: true,
              consent_version: CONSENT_VERSION,
              cookie_categories: cookieCategories,
              consent_timestamp: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            });

          if (!error) {
            setConsent(newConsent);
            return; // Success - exit retry loop
          }

          // Check if error is retryable (FK violation)
          if (isRetryableError(error) && attempt < RETRY_CONFIG.maxAttempts) {
            const delayMs = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt - 1);
            console.log(`[Cookie Consent] FK error on attempt ${attempt}, retrying in ${delayMs}ms...`);
            await sleep(delayMs);
            lastError = error;
            continue;
          }

          // Non-retryable error or max attempts reached
          console.error('Error saving consent to database:', error);
          lastError = error;
          break;
        } catch (error) {
          console.error('Error in saveConsent (database):', error);
          lastError = error;

          // Check if exception contains retryable error
          if (isRetryableError(error) && attempt < RETRY_CONFIG.maxAttempts) {
            const delayMs = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt - 1);
            await sleep(delayMs);
            continue;
          }
          break;
        }
      }

      // If all retries failed, show toast and apply locally
      if (lastError) {
        console.warn('[Cookie Consent] Failed to save after retries, will retry on next interaction');
        toast({
          title: 'Preferences not saved',
          description: 'Your cookie preferences could not be saved. They will be applied locally and saved when connection is restored.',
          variant: 'destructive',
        });
        setConsent(newConsent);
      }
    } else {
      // Save to localStorage for non-authenticated users
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newConsent));
        setConsent(newConsent);
      } catch (error) {
        console.error('Error saving consent to localStorage:', error);
        toast({
          title: 'Preferences not saved',
          description: 'Could not save your cookie preferences locally. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const withdrawConsent = async () => {
    const withdrawnConsent: CookieConsent = {
      consentGiven: true,
      consentVersion: CONSENT_VERSION,
      cookieCategories: {
        necessary: true, // Always true
        analytics: false,
        marketing: false,
      },
      consentTimestamp: new Date().toISOString(),
    };

    // Only save to database if we have a confirmed authenticated user with valid session
    if (isAuthenticated) {
      // Retry logic for FK errors (race condition with auth.users visibility after signup)
      let lastError: unknown = null;
      for (let attempt = 1; attempt <= RETRY_CONFIG.maxAttempts; attempt++) {
        try {
          const { error } = await supabase.from('user_preferences_cookies').upsert({
            user_id: user.id,
            consent_given: true,
            consent_version: CONSENT_VERSION,
            cookie_categories: {
              necessary: true,
              analytics: withdrawnConsent.cookieCategories.analytics,
              marketing: withdrawnConsent.cookieCategories.marketing
            },
            consent_timestamp: new Date().toISOString()
          }, {
            onConflict: 'user_id'
          });

          if (!error) {
            setConsent(withdrawnConsent);
            return; // Success
          }

          // Check if error is retryable (FK violation)
          if (isRetryableError(error) && attempt < RETRY_CONFIG.maxAttempts) {
            const delayMs = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt - 1);
            console.log(`[Cookie Consent] FK error on attempt ${attempt}, retrying in ${delayMs}ms...`);
            await sleep(delayMs);
            lastError = error;
            continue;
          }

          console.error('Error withdrawing consent in database:', error);
          lastError = error;
          break;
        } catch (error) {
          console.error('Error in withdrawConsent (database):', error);
          lastError = error;

          if (isRetryableError(error) && attempt < RETRY_CONFIG.maxAttempts) {
            const delayMs = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt - 1);
            await sleep(delayMs);
            continue;
          }
          break;
        }
      }

      if (lastError) {
        throw lastError; // withdrawConsent should throw on failure (user explicitly withdrawing)
      }
    } else {
      // Update localStorage for non-authenticated users
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(withdrawnConsent));
        setConsent(withdrawnConsent);
      } catch (error) {
        console.error('Error saving withdrawn consent to localStorage:', error);
        throw error;
      }
    }
  };

  return { consent, loading, saveConsent, withdrawConsent };
};
